import type { CompanyAddress } from '@/lib/company/companyAddresses'
import type { GroupementModuleKey } from '@/lib/groupement/groupementModules'
import CompanyType from '@/types/CompanyType'
import type { UserDetail } from '@/types/User'

export type CompanyStatistics = {
  generics?: string
  emailStore?: string
  amountSound?: number
  statusSound?: string
  statusStore?: string
  amountInsight?: number
  statusInsight?: string
  campaignsSound?: number
  statusReporting?: string
  totalSpendStore?: string
  nbSurveysInsight?: number
  totalOrdersStore?: number
  storeLastActivity?: string
  lastOrderDateStore?: string
}

// Motifs de la case « Contrôle à faire » — meme liste que l'enum API ControlTodoReason.
export type ControlTodoReasonCode =
  | 'eurodep_email_conflict'
  | 'eurodep_email_duplicate'
  | 'insee_siret_discrepancy'
  | 'store_siret_alert'
  | 'pharmupp_moving_ambiguous'
  | 'legacy_unknown'

/**
 * Niveau d'activite de la pharmacie (`App\Enum\Company\CompanyActivityLevel`), calcule par le
 * serveur sur la derniere facture SIGNEE Insight, Sound ou PLV : `active` (< 3 mois),
 * `dormant` (> 3 mois), `new` (aucune). Declare ICI et non dans un fichier a part : ce
 * fichier est dans le shell, un import de plus y compterait un module de plus.
 */
export type CompanyActivityLevel = 'active' | 'dormant' | 'new'

type Company = {
  id: number
  companyName?: string
  denominationLegale?: string
  cip?: string
  address?: string
  city?: string
  zipCode?: string
  latitude?: string
  longitude?: string
  administrativeRegion?: string
  siret?: string
  /** Adresses secondaires (D-11) : porte d'entrée GERS, établissement INSEE, siège, livraison. */
  addresses?: CompanyAddress[]
  // Activite INSEE de l'etablissement porte par le SIRET (code sans point, ex. `4773Z`).
  // Suit le SIRET : l'API l'efface quand il change, le controle INSEE pose le nouveau.
  naf?: string | null
  type?: CompanyType
  ownerUser?: UserDetail
  users?: UserDetail[]
  tva?: string
  manageBilling?: boolean
  budgetLimited?: string
  priority?: number
  companyIdStore?: number
  statusStore?: string
  capital?: string
  contactEmail?: string
  legalStatus?: string
  rcsCity?: string
  country?: string
  additionalInformations?: string
  phone?: string
  mobile?: string
  fax?: string
  legalRepresentativeName?: string
  functionLegalRepresentative?: string
  validationStatus?: string
  description?: string
  statusSchool?: string
  statusStripeStore?: string
  createdAt?: string
  updatedAt?: string
  totalCredit?: number
  manager?: UserDetail
  avatarImage?: string
  planIdStore?: number
  // Lot 8 : remplacement de produit dans les commandes Store, active vendeur par vendeur (admin)
  storeProductReplacementEnabled?: boolean
  /** Bouton « Proposer avec l'IA » des fiches produit, activé vendeur par vendeur (Gestion Store). */
  storeProductAiEnabled?: boolean
  /** Page « Analyse des formations » du laboratoire, ouverte par défaut (Gestion School). */
  schoolAnalyticsEnabled?: boolean
  schoolRecommendationOtherLabsEnabled?: boolean
  /** Nouvel espace vendeur Store ouvert en production pour ce vendeur (Gestion Store). */
  storeExtranetBeta?: boolean
  subscriptionStore?: string
  segmentationComplete?: boolean
  stripeStoreCustomerId?: string
  stripeStoreAccountConnectId?: string
  statistics?: CompanyStatistics
  planIdSchool?: number
  subscriptionSchool?: string
  stripeSchoolCustomerId?: string
  sacemSound?: string
  subscriptionSound?: string
  subscriptionSoundType?: string
  userIdStore?: string
  groupement?: Company
  pharmacieSound: PharmacieSound
  contractOspharm?: boolean
  disabledUpdateGroupementByApi?: boolean
  siretProcedureEncours?: boolean
  siretLastControlProcedure?: string
  plateformeAgreee?: boolean | null
  plateformeAgreeeCheckedAt?: string | null
  plateformeAgreeePlatformId?: string | null
  potentialGenericsGers?: string
  /**
   * Code UGA (zonage du référentiel des points de vente). Lecture seule pour la pharmacie :
   * l'écriture est réservée aux administrateurs (section Administration de la fiche) et à
   * l'import / mise à jour des pharmacies — le serveur restaure la valeur pour tout autre appelant.
   */
  codeUga?: string | null
  /**
   * Mode de zonage de ce propriétaire de zones (labo, groupement, chaîne).
   * `company` (défaut) = on sélectionne des pharmacies ; `uga` = on rattache des codes UGA et les
   * pharmacies suivent. Écriture réservée à un ADMIN Pharmedigroup (garde-fou serveur) : le Siège
   * du labo ne peut pas l'activer lui-même, et rien de la mécanique UGA n'est visible tant que le
   * mode vaut `company`.
   */
  zoneAssignmentMode?: 'company' | 'uga' | null
  partners?: Company[]
  numberUsersTeams?: number
  sensitiveFolder?: boolean
  commentInternal?: string
  activeContractStore?: boolean
  activeContractSchool?: boolean
  activeContractInsight?: boolean
  activeContractSound?: boolean
  activeContractMandatSacem?: boolean
  activeContractStoreBag?: boolean
  viatris?: string
  currentQuotaCount?: number
  maxQuotaCount?: number
  activeSound?: boolean
  // Partenariats par module portes par la FICHE (fiches labo / groupement) : regle de
  // recherche des donneurs d'ordre dans Sound, Insight et PLV / Sacherie. A ne pas
  // confondre avec `activeSound` (radio active en pharmacie).
  soundActive?: boolean
  insightActive?: boolean
  storeBagActive?: boolean
  activeEspacePersonnalise?: boolean
  // Fiche INDUSTRY : ouvre le partenariat COTE PHARMACIEN (parcours d'onboarding et ecran
  // « Mes partenaires »). Tant qu'il est faux, les officines peuvent etre rattachees au labo
  // sans que celui-ci apparaisse dans leur espace.
  onboardingActive?: boolean
  // Fiche INDUSTRY : lecture PAR CAMPAGNE SEULE. Dans l'espace labo, le DR et le delegue n'ont
  // ni detail par programme, ni cluster, ni contrat cadre en colonne ; cote officine, « Mes
  // campagnes » rend une ligne par campagne du laboratoire. Reglage admin, faux par defaut.
  campaignOnlyReading?: boolean
  // Date de debut de consultation des donnees. Fiche INDUSTRY : seuls les programmes dont la
  // dateStart est >= a cette date sont visibles dans l'espace labo. Fiche GROUPEMENT / CHAIN :
  // borne basse de toutes les donnees du rapport d'activite. Null = pas de filtre.
  programsVisibleFrom?: string | null
  // Presence au referentiel officiel GERS au dernier import : 'present' | 'absent' | null (jamais
  // controlee : hors France ou aucun import). Posee par l'import GERS, jamais saisie.
  gersStatus?: 'present' | 'absent' | null
  gersCheckedAt?: string | null
  // Fiche GROUPEMENT / CHAIN : modules ouverts a l'espace (cles `lib/groupement/groupementModules`).
  // Null = defaut du catalogue (School seul). Borne la matrice « Acces par module », les menus
  // des membres et le rapport d'activite. Ecriture admin-only.
  groupementModules?: GroupementModuleKey[] | null
  // Fiche URPS : territoire, en debuts de code postal. Ecriture admin-only.
  urpsZipPrefixes?: string[] | null
  // Pharmacie : nom de l'URPS dont elle depend, deduit de son code postal.
  urpsName?: string | null
  // Personnalisation de l'email d'invitation partenaire (companies INDUSTRY).
  activeSacem?: boolean
  activeStoreVendor?: boolean
  activeStorePharmacy?: boolean
  activeSchoolIndustry?: boolean
  controlLastDate?: string
  /** Date de l'état INSEE contre lequel la fiche a été comparée (base Sirene locale ou API). */
  inseeAsOf?: string | null
  /**
   * Fiche CLOSE (plan « une fiche par SIREN ») : date de fin de la période de la société, et la
   * fiche de l'officine qui la continue. Null sur une fiche vivante.
   */
  closedAt?: string | null
  successorCompanyId?: number | null
  predecessorCompanyId?: number | null
  /** `local` (base Sirene locale) ou `api`. */
  inseeSource?: 'local' | 'api' | null
  statusSociety?: string
  otherSource?: string
  controlTodo?: boolean
  // Pourquoi la case « Contrôle à faire » est cochee : un motif par controle qui l'a levee
  // (App\Enum\Company\ControlTodoReason). `at` au format « Y-m-d H:i », heure de Paris.
  controlTodoReasons?: Array<{ code: ControlTodoReasonCode; detail?: string; at?: string }> | null
  // Detail de l'alerte SIRET boutique (lot T-E) : pose avec controlTodo, efface a la validation
  // du controle. `checkedAt` au format « Y-m-d H:i », heure de Paris.
  storeSiretAnomalies?: {
    checkedAt: string
    anomalies: Array<{ type: string; criticite: string; detail: string }>
  } | null
  controlStoreScore?: number
  // Pose par la meme passe de nuit que controlStoreScore : le nom du titulaire de la
  // fiche differe-t-il de celui du compte Store ? null = rien a comparer.
  controlStoreOwnerNameMismatch?: boolean
  // Derniere facture signee Insight, Sound ou PLV (pose a la signature) et niveau d'activite
  // qui en decoule — chantier SIRET, lot 4. Null = aucune facture signee.
  lastSignedInvoiceAt?: string | null
  /** Fin du programme en cours (AAAA-MM-JJ) : avec la facture, fonde le niveau d'activité. */
  runningProgramUntil?: string | null
  activityLevel?: CompanyActivityLevel | null
  subscriptions: [CompanySubscription]
  segmentation?: {
    hoursOpen?: Array<{
      day: string
      begin: string
      end: string
      begin2?: string
      end2?: string
    }>
  }
}

type PharmacieSound = {
  id: number
  distributor: string
  enabled: boolean
}

export type CompanySubscription = {
  id: number
  entity: string
  type: string
  typeVariant: string
  employeeRange: string | null
  subscriptionStatus: string
  stripeCustomerId: string
  stripeSubscriptionId: string
  stripeStatus: string
  startDate: string
  endDate: string
  minEndDate: string
  note: string
  createdAt: string
  updatedAt: string
}

/**
 * Motif d'un changement de titulaire (« Ajouter un nouveau titulaire », chantier « Qualité des
 * fiches », lot 6). Même liste que l'enum PHP `App\Enum\Company\HolderChangeReason`
 * (@group-api) : les changer ensemble. « Autre » exige un détail.
 *
 * Défini ICI et non dans `lib/company/holderChangeReason.ts` (qui le réexporte) : le schéma Zod
 * de `src/ApiMutationEndpoint.ts` le lit, et ce fichier est déjà chargé par le shell — un module
 * de plus dans l'arbre synchrone ferait dépasser `scripts/check-shell-budget.cjs`.
 */
export const HOLDER_CHANGE_REASONS = ['cession', 'correction', 'co_holder_promoted', 'other'] as const

export type HolderChangeReasonCode = (typeof HOLDER_CHANGE_REASONS)[number]

export const HOLDER_CHANGE_REASON_REQUIRING_DETAIL: HolderChangeReasonCode = 'other'

/**
 * « Correction du titulaire » n'est qu'un LIBELLÉ : avec une autre adresse e-mail, les règles
 * créent un nouveau compte et historisent l'actuel comme pour une cession. Le formulaire
 * l'annonce quand c'est le cas.
 */
export const HOLDER_CHANGE_REASON_CORRECTION: HolderChangeReasonCode = 'correction'

export type { Company as default }
